import React, { Suspense, useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, Center, Bounds, Html, useProgress } from '@react-three/drei';

const MODEL_PATH = `${process.env.PUBLIC_URL}/kepalaodiq.glb`;
const BASE_YAW = -Math.PI / 9; // ~20 degrees to the left as the resting pose
const MAX_YAW = 0.55; // radians (~31 degrees) head turns left/right following the cursor
const MAX_PITCH = 0.3; // radians (~17 degrees) head tilts up/down following the cursor
const IDLE_YAW = 0.3; // sway amplitude on devices without a mouse
const IDLE_PITCH = 0.08;
const DRAG_SPEED = 0.012;
const CLICK_THRESHOLD = 6; // px of movement below which a pointer press counts as a click

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const ModelLoader = () => {
  const { progress } = useProgress();
  return (
    <Html center>
      <div className="model-loader">
        <div className="model-loader__ring" />
        <span className="model-loader__text">{Math.round(progress)}%</span>
      </div>
    </Html>
  );
};

// no hover capability = touch device, so the head cannot follow a cursor
const hasMouse = typeof window !== 'undefined'
  ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
  : true;

const HeadModel = () => {
  const groupRef = useRef();
  const follow = useRef({ x: 0, y: 0 });
  const offset = useRef({ yaw: 0, pitch: 0 });
  const drag = useRef(null);
  const { scene } = useGLTF(MODEL_PATH);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (event.pointerType === 'mouse') {
        follow.current.x = (event.clientX / window.innerWidth) * 2 - 1;
        follow.current.y = (event.clientY / window.innerHeight) * 2 - 1;
      }

      if (drag.current) {
        const dx = event.clientX - drag.current.lastX;
        const dy = event.clientY - drag.current.lastY;
        drag.current.lastX = event.clientX;
        drag.current.lastY = event.clientY;
        drag.current.moved += Math.abs(dx) + Math.abs(dy);
        offset.current.yaw += dx * DRAG_SPEED;
        offset.current.pitch = clamp(offset.current.pitch + dy * DRAG_SPEED, -0.5, 0.5);
      }
    };

    const handlePointerUp = () => {
      if (!drag.current) return;
      if (drag.current.moved < CLICK_THRESHOLD) {
        // a plain click/tap spins the head a full turn
        offset.current.yaw += Math.PI * 2;
      } else {
        // wrap so the head returns to the front the shortest way
        const twoPi = Math.PI * 2;
        offset.current.yaw = ((offset.current.yaw + Math.PI) % twoPi + twoPi) % twoPi - Math.PI;
      }
      drag.current = null;
    };

    const handlePointerCancel = () => {
      drag.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, []);

  useFrame(({ clock }, delta) => {
    const group = groupRef.current;
    if (!group) return;

    const dragging = Boolean(drag.current);
    if (!dragging) {
      const decay = Math.min(1, delta * 3);
      offset.current.yaw -= offset.current.yaw * decay;
      offset.current.pitch -= offset.current.pitch * decay;
    }

    // touch devices get a gentle idle sway instead of cursor tracking
    const baseYaw = hasMouse
      ? follow.current.x * MAX_YAW
      : Math.sin(clock.elapsedTime * 0.7) * IDLE_YAW;
    const basePitch = hasMouse
      ? follow.current.y * MAX_PITCH
      : Math.sin(clock.elapsedTime * 0.5) * IDLE_PITCH;

    const targetYaw = baseYaw + offset.current.yaw;
    const targetPitch = basePitch + offset.current.pitch;
    const smoothing = Math.min(1, delta * (dragging ? 15 : 6));
    group.rotation.y += (targetYaw - group.rotation.y) * smoothing;
    group.rotation.x += (targetPitch - group.rotation.x) * smoothing;
  });

  return (
    <group
      ref={groupRef}
      onPointerDown={(event) => {
        event.stopPropagation();
        drag.current = { lastX: event.clientX, lastY: event.clientY, moved: 0 };
      }}
    >
      <Center>
        <primitive object={scene} rotation={[0, BASE_YAW, 0]} />
      </Center>
    </group>
  );
};

const Head3D = () => {
  return (
    <div className="home__model" aria-label="3D model of Syubbanul Siddiq's head">
      <Canvas
        camera={{ position: [0, 0, 2.5], fov: 45 }}
        dpr={[1, 2]}
        style={{ touchAction: 'pan-y' }}
      >
        <ambientLight intensity={1.6} />
        <hemisphereLight intensity={0.8} groundColor="#444444" />
        <directionalLight position={[2, 2, 3]} intensity={2.2} />
        <directionalLight position={[-2, 1, 2]} intensity={1.1} />
        <directionalLight position={[0, -1, -3]} intensity={0.5} />
        <Suspense fallback={<ModelLoader />}>
          <Bounds fit observe margin={1.1}>
            <HeadModel />
          </Bounds>
        </Suspense>
      </Canvas>
    </div>
  );
};

useGLTF.preload(MODEL_PATH);

export default Head3D;
